/**
 * Per-person preferences with an account sync and a local offline copy. Every storage access
 * is wrapped; switching accounts never carries another person's sharing policy across.
 */
import { create } from 'zustand';
import { api } from './api';

export type Theme = 'dark' | 'daylight';
export type Basemap = 'dark' | 'daylight' | 'satellite';
export type Units = 'aviation' | 'metric';

export interface Prefs {
  theme: Theme;
  basemap: Basemap;
  units: Units;
  /** Mute browser notifications + sounds for alerts on this device. */
  muteAlerts: boolean;
  /** Map orientation when following an aircraft. */
  trackUp: boolean;
  /** Trail length in minutes. */
  trailMinutes: number;
  /**
   * Share my location automatically whenever a mission I'm on is live (default on). The phone's
   * own location permission still decides; this is the in-app switch (More → Settings).
   */
  shareLocation: boolean;
  /** Show other ADS-B traffic (not on the mission) on the map. */
  showTraffic: boolean;
  /** Show each mission aircraft's whole flight path since the mission started, not just the recent trail. */
  showFlightPath: boolean;
  /**
   * LIVE viewing mode last picked on this device ('track' | 'coverage' | 'team' | 'conditions' | 'all');
   * null = not picked yet → the role default (features/live/modes.ts validates it).
   */
  liveMode: string | null;
  /** Map view last chosen on this device ('2d' default | '3d'). */
  mapView: '2d' | '3d';
}

let activeUser: string | null = null;
let generation = 0;
let revision = 0;
let saveQueue: Promise<void> = Promise.resolve();
const keyFor = () => activeUser ? `sightline.prefs.${activeUser}` : 'sightline.prefs.anonymous';
const pendingKey = () => `${keyFor()}.pending`;
const markPending = () => safeSet(pendingKey(), '1');
const markSynced = () => safeSet(pendingKey(), '0');

export const DEFAULT_PREFS: Prefs = {
  theme: 'dark',
  basemap: 'dark',
  units: 'aviation',
  muteAlerts: false,
  trackUp: false,
  trailMinutes: 10,
  shareLocation: true,
  showTraffic: true,
  showFlightPath: true,
  liveMode: null,
  mapView: '2d',
};

export function readPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(keyFor());
    if (!raw) {
      // Before account scoping, location opt-out lived on the device. Preserve that refusal
      // until this person explicitly chooses otherwise, without assigning other legacy settings.
      const legacy = localStorage.getItem('apos.prefs.v1');
      return { ...DEFAULT_PREFS, ...(legacy && JSON.parse(legacy)?.shareLocation === false ? { shareLocation: false } : {}) };
    }
    return validatePrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function validatePrefs(input: unknown): Prefs {
  const out = { ...DEFAULT_PREFS };
  if (!input || typeof input !== 'object') return out;
  const p = input as Record<string,unknown>;
  if (p.theme === 'dark' || p.theme === 'daylight') out.theme=p.theme;
  if (p.basemap === 'dark' || p.basemap === 'daylight' || p.basemap === 'satellite') out.basemap=p.basemap;
  if (p.units === 'aviation' || p.units === 'metric') out.units=p.units;
  for (const key of ['muteAlerts','trackUp','shareLocation','showTraffic','showFlightPath'] as const) if(typeof p[key] === 'boolean') out[key]=p[key];
  if(typeof p.trailMinutes === 'number' && Number.isInteger(p.trailMinutes) && p.trailMinutes >= 1 && p.trailMinutes <= 120) out.trailMinutes=p.trailMinutes;
  if(p.liveMode === null || ['track','coverage','team','conditions','all'].includes(String(p.liveMode))) out.liveMode=p.liveMode as string | null;
  if (p.mapView === '2d' || p.mapView === '3d') out.mapView = p.mapView;
  return out;
}

function writePrefs(p: Prefs): void {
  try {
    localStorage.setItem(keyFor(), JSON.stringify(p));
  } catch {
    /* storage unavailable */
  }
}

export function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

interface PrefsStore extends Prefs {
  syncStatus: 'saved' | 'saving' | 'offline';
  set<K extends keyof Prefs>(key: K, value: Prefs[K]): void;
  toggleTheme(): void;
}

export const usePrefs = create<PrefsStore>((set, get) => ({
  ...readPrefs(),
  syncStatus: 'saved',
  set(key, value) {
    set({ [key]: value } as Partial<PrefsStore>);
    writePrefs(currentPrefs());
    if (activeUser) {
      markPending();
      const captured = generation;
      const edit = ++revision;
      const snapshot = currentPrefs();
      set({ syncStatus: 'saving' });
      saveQueue = saveQueue.then(async () => {
        if (captured !== generation) return;
        try {
          await api.preferences.update(snapshot);
          if (captured === generation && edit === revision) { markSynced(); set({ syncStatus: 'saved' }); }
        } catch {
          if (captured === generation && edit === revision) set({ syncStatus: 'offline' });
        }
      });
    }
    if (key === 'theme') applyTheme(value as Theme);
  },
  toggleTheme() {
    get().set('theme', get().theme === 'dark' ? 'daylight' : 'dark');
  },
}));

export function currentPrefs(): Prefs {
  const s = usePrefs.getState();
  return Object.fromEntries(Object.keys(DEFAULT_PREFS).map((k) => [k, s[k as keyof Prefs]])) as unknown as Prefs;
}

/** Scope cached settings to the signed-in person, then read that person's server copy. */
export async function activatePrefs(userId: string | null): Promise<void> {
  activeUser = userId;
  const captured = ++generation;
  revision = 0;
  const cached = readPrefs();
  usePrefs.setState({ ...DEFAULT_PREFS, ...cached, syncStatus: 'saved' });
  applyTheme(cached.theme);
  if (!userId) return;
  try {
    const p = await api.preferences.get();
    if (captured !== generation) return;
    const pending = safeGet(pendingKey()) === '1';
    const next = validatePrefs(pending ? { ...p, ...cached } : { ...cached, ...p });
    usePrefs.setState(next);
    writePrefs(next);
    applyTheme(next.theme);
    if (pending) await retryPrefs();
  } catch {
    if (captured === generation) usePrefs.setState({ syncStatus: 'offline' });
  }
}

export async function retryPrefs(): Promise<void> {
  if (!activeUser) return;
  const captured = generation;
  usePrefs.setState({ syncStatus: 'saving' });
  try {
    await saveQueue;
    if (captured !== generation) return;
    const edit = revision;
    const snapshot = currentPrefs();
    markPending();
    await api.preferences.update(snapshot);
    if (captured === generation && edit === revision) { markSynced(); usePrefs.setState({ syncStatus: 'saved' }); }
  } catch {
    if (captured === generation) usePrefs.setState({ syncStatus: 'offline' });
  }
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  const color = theme === 'dark' ? '#0A0B0D' : '#FFFFFF';
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', color));
}
